import { Link, useLoaderData, useRevalidator } from "react-router-dom";
import { Avatar } from "../components/Avatar";
import { GroupHeader, Panel, PanelAction, PanelHead } from "../components/Panel";
import { HealthDot } from "../components/HealthDot";
import { LoadBar, LoadLegend } from "../components/LoadBar";
import { PilotOnly } from "../components/PilotOnly";
import { PriorityBadge } from "../components/Priority";
import { NewRequestButton } from "../components/NewRequestButton";
import { createRequest } from "../data/api";
import { NEXT_TONE_CLASS } from "../data/status";
import type { DashboardData } from "../data/loaders";
import type { AttentionItem, Delivery, Priority } from "../data/types";

/** Consecutive deliveries sharing a group render under one heading. */
function groupDeliveries(deliveries: Delivery[]) {
  const groups: Array<{ label: string; tone: Delivery["groupTone"]; items: Delivery[] }> = [];
  for (const d of deliveries) {
    const last = groups[groups.length - 1];
    if (last && last.label === d.group) last.items.push(d);
    else groups.push({ label: d.group, tone: d.groupTone, items: [d] });
  }
  return groups;
}

function headerTone(tone: Delivery["groupTone"]): "overdue" | "today" | "neutral" {
  return tone === "overdue" ? "overdue" : tone === "today" ? "today" : "neutral";
}

function Empty({ children }: { children: string }) {
  return <p className="px-4 py-6 text-center text-sm text-[var(--text-2)]">{children}</p>;
}

function DeliveryRow({ delivery }: { delivery: Delivery }) {
  return (
    <a
      href={`/requests/${delivery.id}`}
      className="grid min-h-9 grid-cols-[26px_minmax(0,1fr)_24px_92px] items-center gap-3 border-b border-[var(--border)] px-4 py-2 transition-colors last:border-b-0 hover:bg-gray-100 focus-visible:focus-ring"
    >
      <PriorityBadge priority={delivery.priority} />
      <span className="min-w-0">
        <span className="block truncate text-sm">{delivery.title}</span>
        <span className="block truncate text-xs font-medium text-[var(--text)]">{delivery.account}</span>
      </span>
      {delivery.owner ? <Avatar owner={delivery.owner} /> : <span aria-label="Unassigned" className="text-center text-[var(--text-3)]">—</span>}
      <span className={`text-right text-xs font-medium ${NEXT_TONE_CLASS[delivery.dueTone]}`}>{delivery.dueLabel}</span>
    </a>
  );
}

function AttentionRow({ item }: { item: AttentionItem }) {
  return (
    <a
      href={`/requests/${item.id}`}
      className="grid min-h-11 grid-cols-[26px_minmax(0,1fr)] items-center gap-3 px-4 py-1 transition-colors hover:bg-gray-100 focus-visible:focus-ring"
    >
      <PriorityBadge priority={item.priority} />
      <span className="min-w-0">
        <span className="block truncate text-sm">{item.title}</span>
        <span className={`block truncate text-xs ${item.kind === "breach" ? "text-error-600" : "text-warning-700"}`}>{item.reason}</span>
      </span>
    </a>
  );
}

/**
 * The dashboard, every panel computed by /api/dashboard from live rows.
 * Deliveries are commitment dates grouped by day; Needs attention is the SLA
 * derivation; Accounts lists only those with something to say.
 */
export function ThisWeek() {
  const { dashboard, accounts } = useLoaderData() as DashboardData;
  const revalidator = useRevalidator();
  const { deliveries, attention, accountFlags, workload } = dashboard;

  const groups = groupDeliveries(deliveries);
  const breaches = attention.filter((a) => a.kind === "breach");
  const atRisk = attention.filter((a) => a.kind === "at-risk");

  const totals = workload.map((w) => Object.values(w.counts).reduce((a, b) => a + b, 0));
  const busiest = Math.max(...totals, 1);
  const openTotal = totals.reduce((a, b) => a + b, 0);

  return (
    <div className="mx-auto max-w-[1440px] px-6 py-8">
      <header className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-display-xs font-semibold text-gray-900">This week</h1>
          <p className="mt-1 text-sm text-[var(--text-2)]">{dashboard.weekLabel}</p>
        </div>
        <NewRequestButton
          accounts={accounts}
          onCreated={async (payload) => {
            await createRequest(payload);
            revalidator.revalidate();
          }}
        />
      </header>

      <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_380px]">
        <div className="grid gap-5">
          <Panel labelledBy="deliveries-h">
            <PanelHead id="deliveries-h" title="Deliveries" count={deliveries.length}>
              <PanelAction to="/requests">All requests</PanelAction>
            </PanelHead>
            {groups.length === 0 && <Empty>Nothing due — no open request has a commitment date.</Empty>}
            {groups.map((g) => (
              <div key={g.label}>
                <GroupHeader label={g.label} count={g.items.length} tone={headerTone(g.tone)} />
                {g.items.map((d) => (
                  <DeliveryRow key={d.id} delivery={d} />
                ))}
              </div>
            ))}
          </Panel>

          <Panel labelledBy="accounts-h">
            <PanelHead id="accounts-h" title="Accounts">
              <PanelAction to="/accounts">All accounts</PanelAction>
            </PanelHead>
            {accountFlags.length === 0 && <Empty>Nothing to flag.</Empty>}
            {accountFlags.map((a) => (
              <a
                key={a.id}
                href={`/accounts/${a.id}`}
                className="flex min-h-11 items-center justify-between gap-3 border-b border-[var(--border)] px-4 py-2 transition-colors last:border-b-0 hover:bg-gray-100 focus-visible:focus-ring"
              >
                <span className="flex min-w-0 items-center gap-2">
                  <HealthDot health={a.health} />
                  <b className="font-medium">{a.name}</b>
                  <small className="truncate text-xs text-[var(--text-2)]">{a.note}</small>
                </span>
                <Avatar owner={a.owner} />
              </a>
            ))}
          </Panel>
        </div>

        <div className="grid gap-5">
          <Panel labelledBy="attention-h">
            <PanelHead id="attention-h" title="Needs attention" count={attention.length} />
            {attention.length === 0 ? (
              <Empty>All clear — nothing breached, nothing at risk.</Empty>
            ) : (
              <>
                <GroupHeader label="SLA breaches" count={breaches.length} tone="overdue" />
                {breaches.map((a) => (
                  <AttentionRow key={a.id} item={a} />
                ))}
                <GroupHeader label="At-risk commitments" count={atRisk.length} tone="today" />
                {atRisk.map((a) => (
                  <AttentionRow key={a.id} item={a} />
                ))}
              </>
            )}
          </Panel>

          <Panel labelledBy="workload-h">
            <PanelHead id="workload-h" title="Engineering workload" count={`${openTotal} open`}>
              <PilotOnly>
                <PanelAction to="/capacity">Capacity</PanelAction>
              </PilotOnly>
            </PanelHead>
            <div className="py-1.5">
              {workload.map((w) => {
                const total = Object.values(w.counts).reduce((a, b) => a + b, 0);
                const p1 = w.counts[1 as Priority];
                return (
                  <Link
                    key={w.owner.id}
                    to={`/requests?owner=${w.owner.id}`}
                    className="grid grid-cols-[24px_64px_minmax(0,1fr)_24px_44px] items-center gap-3 px-4 py-1.5 text-sm transition-colors hover:bg-gray-100 focus-visible:focus-ring"
                  >
                    <Avatar owner={w.owner} />
                    <span className="truncate">{w.owner.name}</span>
                    <LoadBar counts={w.counts} scale={busiest} />
                    <span className="text-right font-medium">{total}</span>
                    <span className="text-xs text-[var(--text-2)]">{p1 > 0 ? `${p1} P1` : ""}</span>
                  </Link>
                );
              })}
            </div>
            <div className="border-t border-[var(--border)] px-4 py-2">
              <LoadLegend />
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}
